import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { capaInvestigationsService, CapaInvestigation } from '../../services/capaInvestigationsService';
import { capaImpactsService, CapaImpact } from '../../services/capaImpactsService';
import { capaRootCausesService, CapaRootCause } from '../../services/capaRootCausesService';
import { userService, User } from '../../services/userService';
import { filterPickableUsers } from '../../utils/userPickable';
import Toast, { ToastMessage } from '../../components/common/Toast';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import InvestigationHeader from '../../components/capa/investigation/InvestigationHeader';
import ImpactColumn from '../../components/capa/investigation/ImpactColumn';
import RCAColumn from '../../components/capa/investigation/RCAColumn';
import ActionsColumn from '../../components/capa/investigation/ActionsColumn';
import '../../styles/capa.css';
import extractApiError from '../../utils/apiError';

interface Props {
    investigationIdProp?: number;
    onClose?: () => void;
    // Read-only (History) mode. Default false preserves the live route + the
    // editable WorkView modal behaviour exactly. When true, every mutating
    // surface is suppressed (add/link remedial action, save impact/RCA data,
    // submit, lock/unlock, transfer owner, add/edit/remove team members) while
    // all data stays visible. This forces the read-only treatment regardless of
    // inv.is_locked.
    readOnly?: boolean;
}

const InvestigationView: React.FC<Props> = ({ investigationIdProp, onClose, readOnly = false }) => {
    const params = useParams<{ investigationId: string }>();
    const navigate = useNavigate();
    const invId = investigationIdProp ?? Number(params.investigationId);

    const [inv, setInv] = useState<CapaInvestigation | null>(null);
    const [impacts, setImpacts] = useState<CapaImpact[]>([]);
    const [selectedImpactId, setSelectedImpactId] = useState<number | null>(null);
    const [selectedCauseId, setSelectedCauseId] = useState<number | null>(null);
    const [selectedCause, setSelectedCause] = useState<CapaRootCause | null>(null);
    const [actionReloadKey, setActionReloadKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [users, setUsers] = useState<User[]>([]);

    const handleBack = () => {
        if (onClose) onClose();
        else navigate('/capa');
    };

    const loadAll = useCallback(async () => {
        if (!invId) return;
        try {
            setLoading(true);
            const [i, im] = await Promise.all([
                capaInvestigationsService.get(invId),
                capaImpactsService.list(invId),
            ]);
            setInv(i);
            setImpacts(im);
            // Bump so the read-only ActionsColumn summary re-fetches the selected
            // cause's tasks after the CauseAnalysisModal mutates remedial actions
            // (the modal calls onChanged === loadAll, but selectedCause's id is
            // unchanged, so ActionsColumn needs this explicit reload signal).
            setActionReloadKey(k => k + 1);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load investigation') });
        } finally { setLoading(false); }
    }, [invId]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { userService.getAllUsers({ limit: 200 }).then(r => setUsers(filterPickableUsers(r.data || []))).catch(() => {}); }, []);
    useEffect(() => { setSelectedCauseId(null); setSelectedCause(null); }, [selectedImpactId]);

    useEffect(() => {
        if (!selectedCauseId || !invId || !selectedImpactId) { setSelectedCause(null); return; }
        capaRootCausesService.list(invId, selectedImpactId)
            .then(list => setSelectedCause(list.find(c => c.id === selectedCauseId) || null))
            .catch(() => setSelectedCause(null));
    }, [selectedCauseId, selectedImpactId, invId]);

    if (!invId) return <div style={{ padding: 24 }}>Invalid investigation id.</div>;
    if (loading && !inv) return <div style={{ padding: 24 }}>Loading…</div>;
    if (!inv) {
        return (
            <div style={{ padding: 24 }}>
                <Button variant="fill" icon={ArrowLeft} onClick={handleBack} data-testid="BTN_investigation_back_notfound">Back</Button>
                <p style={{ marginTop: 16 }}>Investigation not found.</p>
            </div>
        );
    }

    // A frozen investigation accepts no mutations of any kind. This is true
    // when: (a) History/archive read-only mode, (b) the investigation is
    // locked (reversible freeze), or (c) it is CLOSED (terminal — no reopen).
    // The backend enforces the same rule authoritatively; this only mirrors it
    // in the UI so the buttons aren't even offered. Every mutating surface
    // (add AND delete/vote/unlink) keys off this single flag.
    const frozen = readOnly || !!inv.is_locked || inv.status === 'CLOSED';

    const columns = (
        <div className="capa-investigation-body">
            <ImpactColumn
                investigationId={invId}
                impacts={impacts}
                selectedImpactId={selectedImpactId}
                onSelect={setSelectedImpactId}
                onChanged={loadAll}
                isLocked={frozen}
            />
            <RCAColumn
                investigationId={invId}
                selectedImpactId={selectedImpactId}
                selectedCauseId={selectedCauseId}
                onSelectCause={setSelectedCauseId}
                users={users}
                onChanged={loadAll}
                isLocked={frozen}
            />
            <ActionsColumn
                selectedCause={selectedCause}
                reloadKey={actionReloadKey}
                onChanged={loadAll}
                readOnly
            />
        </div>
    );

    if (onClose) {
        // Embedded in a Modal — render Modal.Header + Modal.Body directly
        return (
            <>
                <InvestigationHeader investigation={inv} onChanged={loadAll} onBack={onClose} embedded />
                <Modal.Body className="capa-modal-body-flush">
                    {columns}
                </Modal.Body>
                <Toast toast={toast} onClose={() => setToast(null)} />
            </>
        );
    }

    return (
        <div className="capa-investigation-page">
            <InvestigationHeader investigation={inv} onChanged={loadAll} onBack={handleBack} embedded={false} />
            {columns}
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default InvestigationView;
